// WS8 WS-5 — TPS (Third-Party Servicer) detail page at
// /portfolio-admin/tps/:id. Mirrors ServiceProviderDetailPage; the second
// tab is "Loans Assigned" (replacing "Pools Served"). The Loans-assigned
// table is a placeholder until the backend filtered-list endpoint ships.
// SP-3 adds the setup checklist (Overview), the default-TPS explainer, the
// typed fee-schedule editor, and the ?connect=return|refresh landing from
// the Stripe Account-Link round-trip.

import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  Award,
  Banknote,
  Bell,
  Building2,
  CalendarDays,
  CheckSquare,
  FileText,
  Inbox,
  Info,
  KeyRound,
  LayoutDashboard,
  Link as LinkIcon,
  MessageCircle,
  MessageSquare,
  Receipt,
  StickyNote,
} from "lucide-react";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Skeleton,
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
} from "@/design-system/components/ui";
import { OverflowMenu } from "@/design-system/components/ui/OverflowMenu";
import { Modal } from "@/components/ui/Modal";
import {
  EntityTabBar,
  type EntityTabItem,
} from "@/components/layout/EntityTabBar";
import { DocumentPanel } from "@/pages/documents/components/DocumentPanel";
import { TaskListMini } from "@/pages/tasks/components/TaskListMini";
import { AppointmentListMini } from "@/pages/calendar/components/AppointmentListMini";
import { CommunicationsTabPanel } from "@/pages/communications/components/CommunicationsTabPanel";
import { NotesTabPanel } from "@/pages/notes/components/NotesTabPanel";
import { ActivityTabPanel } from "@/pages/activity/components/ActivityTabPanel";
import { RelatedConversationsPanel } from "@/components/messaging/RelatedConversationsPanel";
import { TabPlaceholderCard } from "@/components/DealFlow/common/TabPlaceholderCard";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { extractErrorMessage } from "@/api/client";
import { entitiesApi } from "@/api/counterparties";
import {
  useDeleteTps,
  useInviteTpsPortal,
  useRefreshTpsConnectStatus,
  useSetDefaultTps,
  useTps,
  useTpsPortalAccount,
  useUpdateTps,
} from "@/hooks/portfolioAdmin/useTps";
import { AccountNotificationsPanel } from "@/components/portalAbsorption/AccountNotificationsPanel";
import { PortalAccountTab } from "@/components/portalAbsorption/PortalAccountTab";
import { AccountReviewRequestsTab } from "@/components/portalAbsorption/AccountReviewRequestsTab";
import {
  useDeleteTpsFeeSchedule,
  useTpsFeeSchedule,
} from "@/hooks/portfolioAdmin/useTpsOversight";
import { tpsEntityKeys } from "@/hooks/portfolioAdmin/tpsKeys";
import { hasLinkedEntity, tpsDisplayName } from "@/lib/tpsDisplay";
import { TpsSetupChecklist } from "@/components/portfolioAdmin/tps/TpsSetupChecklist";
import {
  FEE_MODE_LABEL,
  FEE_TYPE_LABEL,
  TpsFeeScheduleModal,
} from "@/components/portfolioAdmin/tpsOversight/TpsFeeScheduleModal";
import {
  formatCalendarDate,
  formatCurrency,
  hasCalendarDayArrived,
} from "@/design-system/lib/format";
import type { TpsFeeScheduleDto } from "@/api/portfolioAdmin/tpsOversightApi";
import type { TpsDto, TpsStatus } from "@/types/tps";
import { formatDate, humanizeStatus } from "../../capital-markets/shared/format";

// §2.1 tab standard (party-surface parity): Overview · Portal account ·
// Review queue & requests · Notifications, then the TPS-specific tabs
// (Loans / Fee Schedule / Certifications), then the six cross-cutting tabs
// last. "portal-review" is the exact id the global Review Queue deep-links
// (?tab=portal-review).
type TabId =
  | "overview"
  | "portal-account"
  | "portal-review"
  | "notifications"
  | "loans"
  | "fees"
  | "certifications"
  | "documents"
  | "tasks"
  | "calendar"
  | "messages"
  | "communications"
  | "notes"
  | "activity";

const TABS: readonly TabId[] = [
  "overview",
  "portal-account",
  "portal-review",
  "notifications",
  "loans",
  "fees",
  "certifications",
  "documents",
  "tasks",
  "calendar",
  "messages",
  "communications",
  "notes",
  "activity",
];

const TAB_ITEMS: ReadonlyArray<EntityTabItem<TabId>> = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  // Portal absorption (§2.1) — in-place account management, replacing the
  // Overview PortalAccountCard mount.
  { key: "portal-account", label: "Portal account", icon: KeyRound },
  { key: "portal-review", label: "Review queue & requests", icon: Inbox },
  // Portal absorption (WS-D2) — broadcasts delivered to the firm's linked
  // portal account, with read state.
  { key: "notifications", label: "Notifications", icon: Bell },
  { key: "loans", label: "Loans Assigned", icon: LinkIcon },
  { key: "fees", label: "Fee Schedule", icon: Receipt },
  { key: "certifications", label: "Certifications", icon: Award },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "tasks", label: "Tasks", icon: CheckSquare },
  { key: "calendar", label: "Calendar", icon: CalendarDays },
  { key: "messages", label: "Messages", icon: MessageSquare },
  { key: "communications", label: "Communications", icon: MessageCircle },
  { key: "notes", label: "Notes", icon: StickyNote },
  { key: "activity", label: "Activity", icon: Activity },
];

const OWNER_TYPE = "tps";
const MESSAGING_TYPE = "tps";

function isTabId(value: string | null): value is TabId {
  return value !== null && (TABS as readonly string[]).includes(value);
}

function TpsStatusBadge({ status }: { status: TpsStatus }) {
  return (
    <Badge status={status === "Active" ? "finalized" : "archived"}>
      {humanizeStatus(status)}
    </Badge>
  );
}

export default function TpsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const tpsQuery = useTps(id);
  const tps = tpsQuery.data;

  const entityQuery = useQuery({
    queryKey: tpsEntityKeys.detail(tps?.entityId),
    queryFn: () => entitiesApi.get(tps!.entityId),
    // #3-A — only fetch when a real entity is linked (entity-less TPS carries
    // the empty GUID, which would 404).
    enabled: hasLinkedEntity(tps?.entityId),
  });
  // SP-3 — the seeded default TPS reads "No servicer assigned", never a
  // missing-name artifact.
  const name = tps
    ? tpsDisplayName(tps, entityQuery.data?.legalEntityName)
    : "TPS Provider";

  const [params, setParams] = useSearchParams();
  const tabParam = params.get("tab");
  const tab: TabId = isTabId(tabParam) ? tabParam : "overview";

  const setTab = useCallback(
    (next: TabId) => {
      const updated = new URLSearchParams(params);
      if (next === "overview") updated.delete("tab");
      else updated.set("tab", next);
      setParams(updated, { replace: true });
    },
    [params, setParams],
  );

  // SP-3 — Stripe Account-Link round-trip landing (?connect=return|refresh).
  // Param consumed once (the ?action=record-payment convention), then a live
  // status re-sync fires for viewers who can manage Stripe. The ref guard is
  // load-bearing: the toast's urgent setState interrupts the router's
  // startTransition navigation, so without it the un-stripped param re-fires
  // the effect forever (toast identity is a dep).
  // Portal absorption (WS-D2/§2.1) — the linked-portal-account reverse lookup
  // feeding the Portal account, Review queue & requests, and Notifications tabs.
  const portalAccount = useTpsPortalAccount(id);

  // §2.1 — the Portal account tab's invite affordance (mirrors the setup
  // checklist's portal-user step; same endpoint + invalidation, gated
  // Tps.Manage). The host owns the toasts (PortalAccountTab contract).
  const invitePortal = useInviteTpsPortal(id ?? "");
  const canManageTps = hasPermission("Tps.Manage");
  const sendPortalInvite = useCallback(() => {
    invitePortal.mutate(undefined, {
      onSuccess: (account) =>
        toast.success(`Invitation sent to ${account.email}.`),
      onError: (err) => toast.error(extractErrorMessage(err)),
    });
  }, [invitePortal, toast]);

  const { mutate: refreshConnectStatus } = useRefreshTpsConnectStatus(id ?? "");
  const canManageStripe = hasPermission("Portfolio.ManageTpsStripe");
  const connectConsumedRef = useRef(false);
  useEffect(() => {
    if (connectConsumedRef.current) return;
    const connect = params.get("connect");
    if (connect !== "return" && connect !== "refresh") return;
    connectConsumedRef.current = true;
    const updated = new URLSearchParams(params);
    updated.delete("connect");
    setParams(updated, { replace: true });
    toast.info(
      connect === "return"
        ? "Welcome back — checking Stripe's verification status…"
        : "That Stripe link expired — generate a fresh one from the checklist.",
    );
    if (id && canManageStripe) refreshConnectStatus();
  }, [params, setParams, id, canManageStripe, refreshConnectStatus, toast]);

  if (tpsQuery.isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">Loading TPS…</p>
      </Card>
    );
  }

  if (!tps) {
    return (
      <Card>
        <h2 className="font-display font-bold text-h2">TPS not found</h2>
        <button
          type="button"
          className="mt-space-3 text-gold-shadow hover:underline text-small"
          onClick={() => navigate("/portfolio-admin/tps")}
        >
          Back to TPS providers
        </button>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-space-3" data-testid="tps-detail-page">
      <TpsHeader tps={tps} name={name} />

      <EntityTabBar
        ariaLabel="TPS sections"
        testIdPrefix="tps"
        items={TAB_ITEMS}
        value={tab}
        onChange={setTab}
      />

      <div className="pt-space-4">
        {tab === "overview" && (
          <OverviewTab
            tps={tps}
            name={name}
            onGoToFees={() => setTab("fees")}
          />
        )}
        {/* Portal absorption (§2.1) — in-place account management. The
            default placeholder never onboards, so it gets no invite. */}
        {tab === "portal-account" &&
          (portalAccount.isLoading ? (
            <p className="text-small text-gray-mid italic m-0">
              Loading portal account…
            </p>
          ) : (
            <PortalAccountTab
              portalUserId={portalAccount.data?.portalUserId ?? null}
              emptyHint="No portal account is linked to this TPS yet — send the portal invite below, or from the setup checklist on the Overview tab."
              invite={
                canManageTps && !tps.isDefault
                  ? { send: sendPortalInvite, pending: invitePortal.isPending }
                  : undefined
              }
            />
          ))}
        {/* §2.1 (C5) — the collection review queue + document requests for the
            firm's portal account. Typically empty by design: TPS money
            approvals stay on the oversight console (stated, never linked). */}
        {tab === "portal-review" &&
          (portalAccount.isLoading ? (
            <p className="text-small text-gray-mid italic m-0">
              Loading portal account…
            </p>
          ) : portalAccount.data?.portalUserId ? (
            <AccountReviewRequestsTab
              scope={{ portalUserId: portalAccount.data.portalUserId }}
              requestComposer={{
                accountType: "Tps",
                email: tps.contactEmail,
                owner: { ownerEntityType: "Tps", ownerEntityId: tps.id },
              }}
              emptyMessage="Nothing has been submitted for review by this servicer yet. TPS money approvals (bills, distributions, 1099s) live in the TPS oversight console, not here."
            />
          ) : (
            <EmptyState
              title="No portal account"
              description="Review queue and document requests appear once this TPS has a linked portal account — invite the firm from the setup checklist on the Overview tab."
            />
          ))}
        {tab === "loans" && <LoansAssignedTab tpsId={tps.id} />}
        {tab === "fees" && <FeeScheduleTab tps={tps} name={name} />}
        {tab === "certifications" && (
          <TabPlaceholderCard
            title="Certifications"
            body="TPS certifications surface here once the certifications module registers this entity type."
          />
        )}
        {/* Portal absorption (WS-D2) — broadcasts delivered to the firm's
            linked portal account. */}
        {tab === "notifications" &&
          (portalAccount.isLoading ? (
            <p className="text-small text-gray-mid italic m-0">
              Loading portal account…
            </p>
          ) : portalAccount.data?.portalUserId ? (
            <AccountNotificationsPanel
              portalUserId={portalAccount.data.portalUserId}
            />
          ) : (
            <EmptyState
              title="No portal account"
              description="This TPS has no linked portal account yet — invite the firm from the setup checklist on the Overview tab, then notifications delivered to it will appear here."
            />
          ))}
        {tab === "documents" && (
          <DocumentPanel
            ownerType={OWNER_TYPE}
            ownerId={tps.id}
            rootName={name}
          />
        )}
        {tab === "tasks" && (
          <TaskListMini
            relatedEntityType="Tps"
            relatedEntityId={tps.id}
            addButtonLabel="+ Add task"
          />
        )}
        {tab === "calendar" && (
          <AppointmentListMini
            relatedEntityType="Tps"
            relatedEntityId={tps.id}
            entityLabel={name}
          />
        )}
        {tab === "messages" && (
          <RelatedConversationsPanel
            resourceType={MESSAGING_TYPE}
            resourceId={tps.id}
            entityLabel={name}
          />
        )}
        {tab === "communications" && (
          <CommunicationsTabPanel
            ownerType={OWNER_TYPE}
            ownerId={tps.id}
            ownerLabel={name}
          />
        )}
        {tab === "notes" && (
          <NotesTabPanel ownerType={OWNER_TYPE} ownerId={tps.id} />
        )}
        {tab === "activity" && (
          <ActivityTabPanel ownerType={OWNER_TYPE} ownerId={tps.id} />
        )}
      </div>
    </div>
  );
}

function TpsHeader({ tps, name }: { tps: TpsDto; name: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("Tps.Manage");
  const setDefault = useSetDefaultTps();
  const update = useUpdateTps(tps.id);
  const del = useDeleteTps();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const isInactive = tps.status === "Inactive";

  const handleSetDefault = () => {
    setDefault
      .mutateAsync(tps.id)
      .then(() => toast.success(`${name} is now the default TPS.`))
      .catch((err) => toast.error(extractErrorMessage(err)));
  };

  const handleToggleActive = () => {
    update
      .mutateAsync({ status: isInactive ? "Active" : "Inactive" })
      .then(() =>
        toast.success(
          isInactive ? `${name} reactivated.` : `${name} deactivated.`,
        ),
      )
      .catch((err) => toast.error(extractErrorMessage(err)));
  };

  const handleDelete = () => {
    del
      .mutateAsync(tps.id)
      .then(() => {
        toast.success(`${name} deleted.`);
        navigate("/portfolio-admin/tps");
      })
      .catch((err) => {
        toast.error(extractErrorMessage(err));
        setConfirmDelete(false);
      });
  };

  return (
    <div className="flex flex-wrap items-center gap-space-3">
      <h1 className="font-display font-bold text-h2 text-onyx m-0">{name}</h1>
      <span className="text-caption font-display uppercase tracking-label text-gray-mid">
        TPS Provider
      </span>
      <TpsStatusBadge status={tps.status} />
      {tps.isDefault && <Badge status="gold">Default</Badge>}
      <div className="ml-auto flex items-center gap-space-2">
        {canManage && !tps.isDefault && (
          <>
            {/* "Set as default" is the placeholder's recovery affordance only —
                a real TPS can never be the default (TpsService.SetDefaultAsync
                rejects it), so gate on identity, not the mutable isDefault. */}
            {tps.isPlaceholder && (
              <Button
                size="sm"
                variant="secondary"
                loading={setDefault.isPending}
                onClick={handleSetDefault}
                data-testid="tps-set-default"
              >
                Set as default
              </Button>
            )}
            <Button
              size="sm"
              variant="secondary"
              loading={update.isPending}
              onClick={handleToggleActive}
              data-testid="tps-toggle-active"
            >
              {isInactive ? "Reactivate" : "Deactivate"}
            </Button>
            <OverflowMenu
              data-testid="tps-actions-menu"
              items={[
                {
                  label: "Delete permanently…",
                  onSelect: () => setConfirmDelete(true),
                },
              ]}
            />
          </>
        )}
      </div>

      {confirmDelete && (
        <Modal
          open
          onClose={() => setConfirmDelete(false)}
          title="Delete TPS provider?"
          description={`Permanently remove ${name}. This only works if no loans are assigned to it — otherwise deactivate it instead.`}
          size="sm"
          footer={
            <div className="flex items-center justify-end gap-space-2">
              <Button
                variant="text"
                size="sm"
                onClick={() => setConfirmDelete(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                variant="destructive"
                loading={del.isPending}
                onClick={handleDelete}
                data-testid="tps-delete-confirm"
              >
                Delete
              </Button>
            </div>
          }
        >
          <p className="text-small text-gray-mid m-0">
            Deleting removes this servicer from the registry. If it currently
            services loans, the delete is blocked — deactivate it instead so
            existing loans keep resolving correctly.
          </p>
        </Modal>
      )}
    </div>
  );
}

/** SP-3 — the built-in placeholder row gets an explainer instead of a
 *  setup checklist: it exists to HOLD unassigned loans, it never onboards. */
function DefaultTpsBanner() {
  return (
    <div
      className="flex items-start gap-space-2 rounded-lg border border-status-info/30 bg-status-info/10 p-space-4"
      data-testid="tps-default-banner"
    >
      <Info size={16} className="text-status-info shrink-0 mt-[2px]" aria-hidden />
      <p className="text-small text-onyx m-0">
        This is the built-in placeholder that holds loans that don&apos;t
        have a servicer yet. It can&apos;t bill or move money. Assign a real
        TPS from a loan&apos;s Overview tab when one is ready.
      </p>
    </div>
  );
}

function OverviewTab({
  tps,
  name,
  onGoToFees,
}: {
  tps: TpsDto;
  name: string;
  onGoToFees: () => void;
}) {
  // §2.1 — the portal-account card moved off Overview into the dedicated
  // Portal account tab.
  return (
    <div className="flex flex-col gap-space-3">
      {tps.isDefault ? (
        <DefaultTpsBanner />
      ) : (
        <TpsSetupChecklist tps={tps} onGoToFees={onGoToFees} />
      )}
      <IdentityCard tps={tps} name={name} />
      <MoneyAndServicingLinksCard />
    </div>
  );
}

/** Portal absorption (WS-D2) — deep links to the money-state surfaces that
 *  deliberately stay on their own consoles (TPS boundary: bills,
 *  distributions, oversight, and money ledgers are cross-party money state
 *  and are never re-hosted on this party-descriptive page). Read-only,
 *  zero data fetches. */
function MoneyAndServicingLinksCard() {
  const links = [
    {
      to: "/portfolio-admin/tps-oversight",
      label: "TPS oversight console",
      detail: "Approval + correction queues, distributions, clearing money totals",
    },
    {
      to: "/portfolio-admin/statements",
      label: "Statement review queue",
      detail: "Pending-review monthly bills across the book",
    },
    {
      to: "/portfolio-admin/loan-payments",
      label: "Loan payments ledger",
      detail: "Recorded servicing payments (collected, net of reversals)",
    },
    {
      to: "/portfolio-admin/income-overview",
      label: "Income overview",
      detail: "Recognized income by classification, channel, and period",
    },
  ];
  return (
    <Card data-testid="tps-money-links-card">
      <h3 className="font-display font-bold text-h3 m-0 mb-space-2 flex items-center gap-space-2">
        <Banknote size={16} aria-hidden /> Money &amp; servicing
      </h3>
      <p className="text-caption text-gray-mid m-0 mb-space-3">
        Bills, distributions, and oversight live on their own consoles — this
        page only describes the firm.
      </p>
      <ul className="list-none p-0 m-0 flex flex-col gap-space-2">
        {links.map((l) => (
          <li key={l.to} className="text-small">
            <Link to={l.to} className="text-gold-shadow hover:underline">
              {l.label}
            </Link>
            <span className="text-gray-mid"> — {l.detail}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function IdentityCard({ tps, name }: { tps: TpsDto; name: string }) {
  return (
    <Card>
      <h3 className="font-display font-bold text-h3 m-0 mb-space-3 flex items-center gap-space-2">
        <Building2 size={16} aria-hidden /> Identity
      </h3>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-space-3 m-0">
        <Field label="Name" value={name} />
        {hasLinkedEntity(tps.entityId) ? (
          <div className="flex flex-col">
            <dt className="text-label font-display uppercase tracking-label text-gray-mid">
              Linked Entity
            </dt>
            <dd className="text-small m-0">
              <Link
                to={`/counterparties/entities/${tps.entityId}`}
                className="text-gold-shadow hover:underline"
              >
                Entity detail
              </Link>
            </dd>
          </div>
        ) : (
          <Field label="Linked Entity" value="None (self-contained contact)" />
        )}
        <Field label="Contact email" value={tps.contactEmail || "—"} />
        <Field label="Contact phone" value={tps.contactPhone || "—"} />
        <Field label="Provider type" value="TPS Provider" />
        <Field label="Agreement date" value={formatDate(tps.agreementDate)} />
        <Field label="Created" value={formatDate(tps.createdAt)} />
        <Field
          label="Last modified"
          value={formatDate(tps.modifiedAt)}
        />
      </dl>
      {tps.notes && (
        <p className="text-small text-gray-mid mt-space-3 mb-0 whitespace-pre-line">
          {tps.notes}
        </p>
      )}
    </Card>
  );
}

function LoansAssignedTab({ tpsId: _tpsId }: { tpsId: string }) {
  // TODO(ws8): wire to /portfolio-admin/loans?tpsId={id} once the loans
  // list endpoint exposes the tpsId filter. Until then, the page renders
  // a placeholder so the tab is real but doesn't fabricate data.
  return (
    <Card>
      <h3 className="font-display font-bold text-h3 m-0 mb-space-2">
        Loans Assigned
      </h3>
      <p className="text-small text-gray-mid italic m-0">
        Loan assignments will surface here in a follow-up — the cross-loan
        filter on the loans list endpoint is pending.
      </p>
    </Card>
  );
}

// SP-3 — typed fee-schedule reader (any Tps.Read viewer) + the shared
// editor modal for Tps.ManageFeeSchedule holders. Percent rows are
// FRACTIONS on the wire (×100 for display).
function formatFeeAmount(row: TpsFeeScheduleDto): string {
  return row.amountType === "Percent"
    ? `${(row.amount * 100).toLocaleString("en-US", {
        maximumFractionDigits: 4,
      })}%`
    : formatCurrency(row.amount, { maximumFractionDigits: 2 });
}

function feeModeLabel(row: TpsFeeScheduleDto): string {
  return FEE_MODE_LABEL[row.mode ?? "deduct_from_payment"] ?? row.mode ?? "—";
}

function FeeRows({
  rows,
  withEffectiveTo,
  actions,
}: {
  rows: TpsFeeScheduleDto[];
  withEffectiveTo?: boolean;
  actions?: {
    onEdit: (row: TpsFeeScheduleDto) => void;
    onDelete: (feeId: string) => void;
  };
}) {
  return (
    <Table>
      <THead>
        <TR>
          <TH>Fee</TH>
          <TH className="text-right">Amount</TH>
          <TH>Collected</TH>
          <TH>Effective from</TH>
          {withEffectiveTo && <TH>Effective to</TH>}
          <TH>Notes</TH>
          {actions && <TH className="text-right">Actions</TH>}
        </TR>
      </THead>
      <TBody>
        {rows.map((row) => (
          <TR key={row.id} data-testid={`fee-row-${row.id}`}>
            <TD className="font-semibold text-onyx">
              {FEE_TYPE_LABEL[row.feeType] ?? row.feeType}
            </TD>
            <TD className="text-right tabular-nums">{formatFeeAmount(row)}</TD>
            <TD className="text-gray-mid">{feeModeLabel(row)}</TD>
            <TD className="text-gray-mid">
              {formatCalendarDate(row.effectiveFrom)}
            </TD>
            {withEffectiveTo && (
              <TD className="text-gray-mid">
                {row.effectiveTo ? formatCalendarDate(row.effectiveTo) : "—"}
              </TD>
            )}
            <TD className="text-gray-mid">{row.notes ?? "—"}</TD>
            {actions && (
              <TD className="text-right">
                <OverflowMenu
                  data-testid={`fee-row-actions-${row.id}`}
                  items={[
                    { label: "Edit…", onSelect: () => actions.onEdit(row) },
                    {
                      label: "Delete",
                      destructive: true,
                      confirm: "Delete this fee row?",
                      onSelect: () => actions.onDelete(row.id),
                    },
                  ]}
                />
              </TD>
            )}
          </TR>
        ))}
      </TBody>
    </Table>
  );
}

function FeeScheduleTab({ tps, name }: { tps: TpsDto; name: string }) {
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("Tps.ManageFeeSchedule");
  const query = useTpsFeeSchedule(tps.id);
  const del = useDeleteTpsFeeSchedule(tps.id);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingFee, setEditingFee] = useState<TpsFeeScheduleDto | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  const rows = query.data ?? [];

  // 🔴 ONE PREDICATE COVERS THIS SITE AND THE LOAN-LEVEL ONE
  // (`paymentTracking/ServicingFeeConfigCard.tsx`) EVEN THOUGH THEY FAILED IN
  // OPPOSITE DIRECTIONS — and that is the whole reason to reach for the shared
  // rule rather than writing a second fix here.
  //
  // Both ask "has this effective date arrived?", but they store the answer on
  // different conventions: the loan card writes UTC MIDNIGHT, while this one
  // goes through `calendarDateInputToIso`, which writes NOON UTC. Compared
  // against an instant those two land on opposite sides of Phoenix's
  // seven-hour offset — a midnight row took effect SEVEN HOURS EARLY, and a
  // noon row took effect FIVE HOURS LATE, so here the SUPERSEDED fee kept
  // showing as current until 05:00 Phoenix on the very day it was replaced.
  //
  // `hasCalendarDayArrived` delegates to `calendarDaysUntil`, which reads the
  // due side in UTC and today in the VIEWER's zone and is TIME-OF-DAY AGNOSTIC
  // on both. So `…T00:00:00Z` and `…T12:00:00Z` for the same day resolve to
  // the same calendar day and the same answer. One rule, no branch on how the
  // value happens to be stored — which also means the loan card's write path
  // can move from midnight to noon without touching either read.
  //
  // Only ONE arm here: `effectiveTo === null` is a plain open-ended test, not
  // a date comparison, so unlike the loan card there is no upper bound to keep
  // in step. A closed row is history by definition.
  //
  // This is a DISPLAY read. The engine still sizes off the server's own
  // instant comparison, so for a few hours a day the two can disagree by one
  // row; the honest reading of a wall-calendar date is the one a human sees.
  const currentRows = rows.filter(
    (r) => r.effectiveTo === null && hasCalendarDayArrived(r.effectiveFrom),
  );
  const currentIds = new Set(currentRows.map((r) => r.id));
  const historyRows = rows.filter((r) => !currentIds.has(r.id));

  const openAdd = () => {
    setEditingFee(null);
    setEditorOpen(true);
  };
  const openEdit = (row: TpsFeeScheduleDto) => {
    setEditingFee(row);
    setEditorOpen(true);
  };
  const closeEditor = () => {
    setEditorOpen(false);
    setEditingFee(null);
  };
  const handleDelete = (feeId: string) => {
    del.mutate(feeId, {
      onSuccess: () => toast.success("Fee row deleted."),
      onError: (err) => toast.error(extractErrorMessage(err)),
    });
  };
  const rowActions = canManage
    ? { onEdit: openEdit, onDelete: handleDelete }
    : undefined;

  return (
    <Card data-testid="tps-fee-schedule-tab">
      <div className="flex flex-wrap items-center justify-between gap-space-3 mb-space-2">
        <h3 className="font-display font-bold text-h3 m-0">Fee Schedule</h3>
        {canManage && (
          <Button
            size="sm"
            onClick={openAdd}
            data-testid="fee-schedule-edit-button"
          >
            Add / update fee
          </Button>
        )}
      </div>
      <p
        className="text-caption text-gray-mid mb-space-3"
        data-testid="fee-schedule-callout"
      >
        The monthly servicing fee is copied onto each loan at assignment.
        Changing it here does NOT change loans already assigned — use
        &ldquo;Re-stamp from TPS schedule&rdquo; on a loan&apos;s fee card to
        update one.
      </p>

      {query.isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : query.isError ? (
        <p className="text-small text-status-error" role="alert">
          Couldn&apos;t load the fee schedule. Try again.
        </p>
      ) : currentRows.length === 0 ? (
        <EmptyState
          title="No fees configured"
          description="This servicer has no current fee-schedule rows yet."
        />
      ) : (
        <FeeRows rows={currentRows} actions={rowActions} />
      )}

      {!query.isLoading && !query.isError && historyRows.length > 0 && (
        <div className="mt-space-3">
          <Button
            variant="text"
            size="sm"
            onClick={() => setShowHistory((v) => !v)}
            data-testid="fee-schedule-history-toggle"
          >
            {showHistory
              ? "Hide history"
              : `Show history (${historyRows.length})`}
          </Button>
          {showHistory && (
            <div className="mt-space-2" data-testid="fee-schedule-history">
              <FeeRows rows={historyRows} withEffectiveTo actions={rowActions} />
            </div>
          )}
        </div>
      )}

      <TpsFeeScheduleModal
        tpsId={tps.id}
        tpsName={name}
        currentRecoveryRate={null}
        open={editorOpen}
        onClose={closeEditor}
        editRow={editingFee ?? undefined}
      />
    </Card>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <dt className="text-label font-display uppercase tracking-label text-gray-mid">
        {label}
      </dt>
      <dd className="text-small text-onyx m-0">{value}</dd>
    </div>
  );
}
